import { getApplicationSettings } from 'owa-application-settings';
import type { MailboxInfo } from 'owa-client-types';
import type { SearchOptionalDiagnosticData } from 'owa-copilot-feedback';
import { isClientEntityRecognitionEnabled } from 'owa-search-service';
import { getEntityRewriteDiagnostics } from 'owa-search-query-parser/lib/getEntityRewriteDiagnostics';
import { getScenarioStore } from 'owa-search-store';
import type SearchScenarioId from 'owa-search-store/lib/store/schema/SearchScenarioId';
import { getEntityRecognitionDiagnosticsSnapshot } from './entityRecognitionDiagnosticsSnapshot';

/**
 * Builds the entity-recognition diagnostics attached to a `Search` scenario
 * thumbs up/down, so feedback submitted through the shared Copilot feedback flow
 * carries the literal query, the matched person alias(es), and the reason codes
 * that explain the rewrite.
 *
 * This payload only leaves the device when the user submits feedback and the
 * tenant's diagnostics-sharing policy allows it — it is never logged to telemetry.
 */
export default function getEntityRecognitionSearchDiagnostics(
    mailboxInfo: MailboxInfo,
    searchScenarioId: SearchScenarioId
): SearchOptionalDiagnosticData {
    const searchStore = getScenarioStore(searchScenarioId);

    // The rewrite only ran for the executed search under gates (supported tab,
    // not a conversation-topic/recipient-expanded/altered query) that the matcher
    // itself does not enforce. Prefer the diagnostics snapshotted at execution time,
    // which exist only when recognition was attempted for the executed query, so
    // feedback reflects what actually ran (including the candidates of a
    // recognized-but-skipped query) and the matcher is never re-run ungated here.
    const applied = searchStore.entityRecognitionApplied;
    const diagnostics =
        getEntityRecognitionDiagnosticsSnapshot(mailboxInfo, searchStore.currentSearchQueryId) ??
        (applied ? getEntityRewriteDiagnostics(searchStore.searchText, mailboxInfo) : undefined);

    const base = {
        client: getApplicationSettings('PlatformType').isMonarch ? 'Monarch' : 'OWA',
        flightEnabled: isClientEntityRecognitionEnabled(mailboxInfo),
        searchSessionGuid: searchStore.searchSessionGuid,
        searchQueryId: searchStore.currentSearchQueryId,
        // The analyzed query (the altered query when a suggestion was accepted), so the
        // candidates below are paired with the text they were computed from.
        query: diagnostics?.rawQuery ?? searchStore.searchText,
    } as const;

    if (!diagnostics && !applied) {
        return {
            ...base,
            rewriteApplied: false,
            skippedReason: 'NotAppliedToExecutedQuery',
            entityCount: 0,
            matchedSpanCount: 0,
            queryTokenCount: 0,
            stopWordCount: 0,
            residualKeywordCount: 0,
            matchedEntities: [],
        };
    }

    return {
        ...base,
        // Never report a rewrite for a query that the executed search did not rewrite.
        rewriteApplied: applied && (diagnostics?.rewriteApplied ?? false),
        skippedReason:
            diagnostics?.skippedReason ?? (applied ? undefined : 'NotAppliedToExecutedQuery'),
        rewrittenQuery: applied ? diagnostics?.rewrittenQuery : undefined,
        entityCount: diagnostics?.entityCount ?? 0,
        matchedSpanCount: diagnostics?.matchedSpanCount ?? 0,
        queryTokenCount: diagnostics?.queryTokenCount ?? 0,
        stopWordCount: diagnostics?.stopWordCount ?? 0,
        residualKeywordCount: diagnostics?.residualKeywordCount ?? 0,
        topMatchLayer: diagnostics?.topMatchLayer,
        topMatchedTextLength: diagnostics?.topMatchedTextLength,
        topTargetNameLength: diagnostics?.topTargetNameLength,
        topRank: diagnostics?.topRank,
        topBucket: diagnostics?.topBucket,
        matchedEntities: (diagnostics?.matchedEntities ?? []).map(entity => ({
            matchedText: entity.matchedText,
            correctedText: entity.correctedText,
            displayName: entity.displayName,
            email: entity.email,
            layer: entity.layer,
            rank: entity.rank,
            bucket: entity.bucket,
        })),
    };
}
